import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Card, Group, Menu, Modal, Select, SimpleGrid, Stack, Text,
  TextInput, Textarea, rem,
  Tooltip,
} from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconCategory, IconChevronDown, IconChevronRight, IconDots, IconEdit, IconFolder, IconFolders, IconLock,
  IconPlus, IconSitemap, IconStack2, IconTag, IconTrash,
} from '@tabler/icons-react';
import { useCrudResource } from '@/shared/hooks/useCrudResource';
import { EmptyState } from '@/shared/components/EmptyState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { StatCard } from '@/shared/components/StatCard';
import { mw, mwSectionLabel } from '@/shared/mobile/tokens';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { notifications } from '@mantine/notifications';

export const Route = createFileRoute('/_app/admin/categories')({ component: AdminCategoriesPage });

interface CategoryRow extends Denormalized {
  type: 'categories';
  id: string;
  name?: string;
  code?: string;
  description?: string;
  /** BE round 2: parent come relationship denormalizzata. */
  parent?: { id: string; type?: string; name?: string } | null;
  /**
   * ⚠️ **`false` = categoria di sistema: non si tocca.**
   *
   * È il campo con cui il BE marca le righe che il prodotto stesso usa (`Category.Erasable`),
   * e Lares lo rispetta da sempre: sull'albero mostra un divieto accanto al nome, e chi prova
   * ad aprirla riceve «Categoria di sistema, non modificabile!» invece dell'editor
   * (`category-crud.js` §123, §185).
   *
   * Da noi non lo guardava nessuno: da React una categoria di sistema si rinominava e si
   * cancellava come le altre. Il danno non è locale — quelle righe le nomina il codice, e una
   * rinominata rompe la classificazione dei contenuti che la usano, in silenzio.
   */
  erasable?: boolean;
}

/** Una riga che il prodotto usa per sé: si legge, non si modifica. */
const system = (c: CategoryRow): boolean => c.erasable === false;

/** Il perché, scritto uguale ovunque lo si debba dire. */
const SYSTEM_REASON = 'Categoria di sistema: la usa il prodotto, e rinominarla o cancellarla ne romperebbe l’uso.';

/**
 * ⚠️ Il secondo vincolo di Lares: **una categoria con figlie non si cancella**
 * (`category-crud.js` §191, «Cancellazione non attiva poiché contiene sotto-categorie!»).
 *
 * Da noi la modale avvisava che le figlie sarebbero rimaste **orfane**, e lasciava fare. Un
 * avviso non è un vincolo: le orfane restano a database con un `parent` che punta al nulla,
 * spariscono dall'albero — che si disegna dalla radice — e il modo di accorgersene è che
 * qualcuno cerca una categoria che «c'era». Qui si nega, e si dice cosa fare prima.
 */
const CHILDREN_REASON = 'Contiene sotto-categorie: spostale o cancellale prima, o resterebbero appese a un genitore che non c’è più.';

interface CategoryFormValues {
  name: string;
  code: string;
  description: string;
  /** Id del parent selezionato dalla Select (vuoto = root). */
  parentCategory: string;
}

const INITIAL: CategoryFormValues = { name: '', code: '', description: '', parentCategory: '' };

/** Nodo albero costruito dalla lista piatta (parent.id → figli). */
interface TreeNode { row: CategoryRow; children: TreeNode[] }

/** Costruisce la foresta ordinata per nome da una lista piatta di categorie. */
function buildForest(rows: CategoryRow[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  rows.forEach((r) => byId.set(r.id, { row: r, children: [] }));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    const pid = node.row.parent?.id;
    const parent = pid ? byId.get(pid) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node); // root, oppure parent fuori pagina → trattato come radice
  }
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => (a.row.name ?? '').localeCompare(b.row.name ?? ''));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

/** Appiattisce la foresta in righe {node, depth, hasChildren} rispettando i nodi collassati. */
function flattenVisible(forest: TreeNode[], collapsed: Set<string>): { node: TreeNode; depth: number }[] {
  const out: { node: TreeNode; depth: number }[] = [];
  const seen = new Set<string>();
  const walk = (nodes: TreeNode[], depth: number) => {
    for (const n of nodes) {
      if (seen.has(n.row.id)) continue; // guardia anti-ciclo
      seen.add(n.row.id);
      out.push({ node: n, depth });
      if (n.children.length && !collapsed.has(n.row.id)) walk(n.children, depth + 1);
    }
  };
  walk(forest, 0);
  return out;
}

function maxDepth(forest: TreeNode[]): number {
  let max = 0;
  const walk = (nodes: TreeNode[], d: number) => {
    if (nodes.length) max = Math.max(max, d);
    nodes.forEach((n) => walk(n.children, d + 1));
  };
  walk(forest, 1);
  return max;
}

function AdminCategoriesPage() {
  const [modalOpened, modal] = useDisclosure(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const form = useForm<CategoryFormValues>({
    mode: 'controlled',
    initialValues: INITIAL,
    validate: {
      name: (v) => (v.trim() ? null : 'Nome obbligatorio'),
    },
  });

  const crud = useCrudResource<CategoryRow>({
    resource: 'categories',
    // Albero costruito client-side: serve l'intero set (non paginato).
    list: { page: { size: 100 }, sort: ['name'] },
    labels: { create: 'Categoria creata', update: 'Categoria aggiornata', remove: 'Categoria eliminata' },
    onCreateSuccess: () => { modal.close(); form.reset(); },
    onUpdateSuccess: () => { modal.close(); form.reset(); },
  });
  const allCategories = crud.items;
  const isLoading = crud.isLoading;
  const isError = crud.query.isError;

  const forest = useMemo(() => buildForest(allCategories), [allCategories]);
  const visibleRows = useMemo(() => flattenVisible(forest, collapsed), [forest, collapsed]);

  const stats = useMemo(() => {
    const roots = forest.length;
    const withChildren = allCategories.filter((c) => allCategories.some((x) => x.parent?.id === c.id)).length;
    return { total: crud.total, roots, withChildren, depth: maxDepth(forest) };
  }, [forest, allCategories, crud.total]);

  const parentOptions = useMemo(
    () => allCategories
      .filter((c) => c.id && c.name)
      .map((c) => ({ value: c.id, label: c.name ?? c.id })),
    [allCategories],
  );

  const parentIdsWithChildren = useMemo(
    () => new Set(allCategories.filter((c) => allCategories.some((x) => x.parent?.id === c.id)).map((c) => c.id)),
    [allCategories],
  );

  const openCreate = () => {
    setEditingId(null);
    form.setValues(INITIAL);
    modal.open();
  };

  const openEdit = (row: CategoryRow) => {
    /* Come Lares: cliccarla non apre l'editor, e dice perché invece di non fare niente — un
       click che non produce nulla si legge come un guasto dell'interfaccia. */
    if (system(row)) {
      notifications.show({ color: 'yellow', title: 'Non modificabile', message: SYSTEM_REASON });
      return;
    }
    setEditingId(row.id);
    form.setValues({
      name: row.name ?? '',
      code: row.code ?? '',
      description: row.description ?? '',
      parentCategory: row.parent?.id ?? '',
    });
    modal.open();
  };

  const onSubmit = form.onSubmit((values) => {
    const attributes = {
      name: values.name.trim(),
      code: values.code.trim() || null,
      description: values.description.trim() || null,
    };
    const relationships = {
      parent: { data: values.parentCategory ? { type: 'categories', id: values.parentCategory } : null },
    };
    if (editingId) crud.update.mutate({ id: editingId, attributes, relationships });
    else crud.create.mutate({ attributes, relationships });
  });

  const onDelete = (c: CategoryRow) => {
    /* ⚠️ Le guardie stanno **qui** e non solo sul menu: un `disabled` si aggira, e questa
       funzione la chiamano due punti. Il server rifiuterebbe comunque, ma con un errore che
       non spiega quale delle due regole ha incontrato. */
    const reason = system(c)
      ? SYSTEM_REASON
      : (parentIdsWithChildren.has(c.id) ? CHILDREN_REASON : null);
    if (reason) {
      notifications.show({ color: 'yellow', title: 'Non si può eliminare', message: reason });
      return;
    }
    modals.openConfirmModal({
      title: 'Elimina categoria',
      children: <Text size="sm">Eliminare «{c.name}»? L’operazione non si annulla.</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => crud.remove.mutate(c.id),
    });
  };

  const toggle = (id: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(parentIdsWithChildren));

  const truncated = crud.total > allCategories.length;

  const isMobile = useIsMobileShell();
  const styleEl = <style>{`.cat-row:hover{background:${mw.surface2};}`}</style>;
  const headActions = <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Nuova categoria</Button>;

  const content = (
      <Stack>
        {isError && <QueryErrorAlert error={crud.query.error} onRetry={() => void crud.query.refetch()} />}

        {isLoading ? (
          <InlineLoader label="Caricamento delle categorie…" minHeight={120} />
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <StatCard icon={IconCategory} color="brand" label="Categorie" value={stats.total} />
            <StatCard icon={IconSitemap} color="azzurro" label="Radici" value={stats.roots} />
            <StatCard icon={IconFolders} color="green" label="Con figli" value={stats.withChildren} />
            <StatCard icon={IconStack2} color="gray" label="Profondità" value={stats.depth} />
          </SimpleGrid>
        )}

        <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
          <Text style={mwSectionLabel}>Struttura</Text>
          <Group gap="xs">
            <Button size="xs" variant="subtle" onClick={expandAll} disabled={parentIdsWithChildren.size === 0}>Espandi tutto</Button>
            <Button size="xs" variant="subtle" onClick={collapseAll} disabled={parentIdsWithChildren.size === 0}>Comprimi tutto</Button>
          </Group>
        </Group>

        <Card withBorder padding={0} radius="lg" shadow="sm">
          {isLoading ? (
            <InlineLoader label="Caricamento delle categorie…" minHeight={120} />
          ) : allCategories.length === 0 ? (
            <EmptyState
              icon={IconCategory}
              title="Nessuna categoria"
              description="Le categorie organizzano i contenuti e compaiono nei filtri di ricerca."
              actionLabel="Nuova categoria"
              onAction={openCreate}
            />
          ) : (
            <div>
              {visibleRows.map(({ node, depth }) => {
                const c = node.row;
                const hasChildren = node.children.length > 0;
                const isCollapsed = collapsed.has(c.id);
                return (
                  <div
                    key={c.id}
                    className="cat-row"
                    onClick={() => openEdit(c)}
                    title={system(c) ? SYSTEM_REASON : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      /* Il cursore lo dice prima del click: su una riga di sistema non è una
                         mano, perché non c'è niente da aprire. */
                      cursor: system(c) ? 'default' : 'pointer',
                      padding: '10px 14px', paddingLeft: 14 + depth * 26,
                      borderBottom: `1px solid ${mw.surface2}`,
                    }}
                  >
                    {hasChildren ? (
                      <ActionIcon
                        variant="subtle" color="gray" size="sm" aria-label={isCollapsed ? 'Espandi' : 'Comprimi'}
                        onClick={(e) => { e.stopPropagation(); toggle(c.id); }}
                      >
                        {isCollapsed ? <IconChevronRight size={16} /> : <IconChevronDown size={16} />}
                      </ActionIcon>
                    ) : (
                      <span style={{ width: 26, flexShrink: 0 }} />
                    )}

                    <span style={{
                      width: 30, height: rem(30), borderRadius: 9, flexShrink: 0,
                      background: hasChildren ? mw.brandTint : mw.accentTint,
                      color: hasChildren ? mw.brand : mw.accentText,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {hasChildren ? <IconFolder size={17} /> : <IconTag size={16} />}
                    </span>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Group gap={8} wrap="nowrap">
                        <Text fw={600} style={{ color: mw.ink900 }} truncate>{c.name}</Text>
                        {/* Il divieto accanto al nome, come in Lares: si vede **prima** di
                            provare, invece di scoprirlo con un click che non fa niente. */}
                        {system(c) && (
                          <Tooltip label={SYSTEM_REASON} withArrow multiline w={260}>
                            <IconLock size={13} style={{ color: mw.ink300, flexShrink: 0 }} aria-label="Categoria di sistema" />
                          </Tooltip>
                        )}
                        {c.code && (
                          <Text
                            component="span"
                            style={{
                              fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.accentText, background: mw.accentTint,
                              borderRadius: 6, padding: '1px 7px', fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                            }}
                          >
                            {c.code}
                          </Text>
                        )}
                        {hasChildren && (
                          <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
                            {node.children.length}
                          </Badge>
                        )}
                      </Group>
                      {c.description && <Text size="xs" c="dimmed" lineClamp={1}>{c.description}</Text>}
                    </div>

                    <Menu withinPortal position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Azioni" onClick={(e) => e.stopPropagation()}>
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          disabled={system(c)}
                          onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                        >
                          Modifica
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          disabled={system(c) || parentIdsWithChildren.has(c.id)}
                          onClick={(e) => { e.stopPropagation(); void onDelete(c); }}
                        >
                          Elimina
                        </Menu.Item>
                        {/* ⚠️ Spente **e spiegate**: due voci grigie senza motivo sono la cosa
                            che fa chiedere «è rotto?». Il testo è lo stesso del tooltip e del
                            messaggio, così chi lo legge due volte legge la stessa frase. */}
                        {(system(c) || parentIdsWithChildren.has(c.id)) && (
                          <Text size="xs" c="dimmed" px={12} py={6} style={{ maxWidth: 260 }}>
                            {system(c) ? SYSTEM_REASON : CHILDREN_REASON}
                          </Text>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {truncated && (
          <Text size="xs" c="dimmed">
            Mostrate le prime {allCategories.length} categorie di {crud.total}. Affina la struttura o contatta lo sviluppo per il supporto a set più grandi.
          </Text>
        )}
      </Stack>
  );

  const modalEl = (
      <Modal
        opened={modalOpened}
        onClose={() => { modal.close(); form.reset(); }}
        title={editingId ? 'Modifica categoria' : 'Nuova categoria'}
        centered
        size="md"
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <TextInput label="Nome" required {...form.getInputProps('name')} />
            <TextInput label="Slug / Code" placeholder="es. fattura-pa" {...form.getInputProps('code')} />
            <Textarea label="Descrizione" autosize minRows={2} {...form.getInputProps('description')} />
            <Select
              label="Parent"
              placeholder="Nessuno (categoria radice)"
              clearable
              searchable
              data={parentOptions.filter((o) => o.value !== editingId)}
              {...form.getInputProps('parentCategory')}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => { modal.close(); form.reset(); }}>Annulla</Button>
              <Button type="submit" loading={crud.create.isPending || crud.update.isPending}>
                {editingId ? 'Salva' : 'Crea'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
  );

  if (isMobile) {
    return (
      <>
        {styleEl}
        <Stack gap={16} px={16} pt={16} pb={24}>
          <MobileHeader title="Categorie" />
          <Group justify="flex-end">{headActions}</Group>
          {content}
        </Stack>
        {modalEl}
      </>
    );
  }

  return (
    <MaeShell>
      {styleEl}
      <MaePageHead
        icon={<IconSitemap size={22} />}
        title="Categorie"
        sub="Categorie dell’azienda in struttura ad albero. Clicca una voce per modificarla."
        actions={headActions}
      />
      {content}
      {modalEl}
    </MaeShell>
  );
}
